import { QueryEntity } from '@datorama/akita';
import { DQOIReferenceDataState } from './dqoi-reference-data.store';
import { DQOIReferenceData } from '@app/cp/shared/models/DQOIReferenceData';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { DQOIReferenceDataStore } from './dqoi-reference-data.store';
import { DQOIService } from '@app/cp/shared/services/DQOI.service';
import { catchError, map, switchMap, tap, filter, take } from 'rxjs/operators';
import { environment } from '@env/environment';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { RefDataTableRow } from '@app/cp/shared/database/db';
import { CommonValues } from '@app/cp/shared/static/CommonValues';
import { CacheItem, CacheType, CPStorageService } from '@app/cp/shared/services/cpstorage.service';

class RefDataCacheItem {
    cacheExpiresAtDate: Date;
    currentDbDate: Date;
}

@Injectable({
    providedIn: 'root'
})
export class DQOIReferenceDataQuery extends QueryEntity<DQOIReferenceDataState, DQOIReferenceData> {
    constructor(protected store: DQOIReferenceDataStore, private doqiService: DQOIService, private dbService: NgxIndexedDBService, private storageService: CPStorageService) {
        super(store);
    }

    fetching: boolean = false;
    private _checkingRefDataLoaded = false;
    private _referenceDataUpdated = new BehaviorSubject<DQOIReferenceData>(null);
    referenceDataUpdated = this._referenceDataUpdated.asObservable();
    private fetchResultSubject = new BehaviorSubject<null | string>(null);

    ensureRefDataIsLoaded(companyId: string, forceReload: boolean = false): Observable<boolean> {
        if (!companyId) {
            return of(false);
        }
        let caller: string;
        if (environment.debug) {
            caller = this.getCaller();
        }
        if (this._checkingRefDataLoaded) {
            return this.fetchResultSubject.pipe(
                tap(() => {
                    if (environment.debug) {
                        console.debug("%c EnsureRefDataIsLoaded: Returning fetchResultSubject due to fetch in progress. CALLER:", CommonValues.LoggingMessage_ColorGrey, caller);
                    }
                }),
                filter(result => result !== null),
                take(1),
                switchMap((r) => {
                    return of(true);
                })
            );
        }

        //return the record in the IndexedDB if we have one, but keep going with the request. This will be like an eventual consistent RefData
        this.getActiveRefData().subscribe((refdataRow: DQOIReferenceData | null) => {
            if (!refdataRow) {
                return;
            }
            if (environment.debug) {
                console.debug("%c Loaded RefData from IndexedDB", CommonValues.LoggingMessage_ColorTeal);
            }
            this._referenceDataUpdated.next(refdataRow);
        });

        this._checkingRefDataLoaded = true;
        return this.isCacheExpired().pipe(
            switchMap((isExpired: boolean) => {
                this.fetchResultSubject.next(null);
                if (isExpired || forceReload) {
                    return this.fetch(companyId, isExpired || forceReload).pipe(
                        switchMap(() => {
                            this._checkingRefDataLoaded = false;
                            this.storageService.del(CommonValues.StorageKeys_ReferenceDataCacheInfo);
                            this.fetchResultSubject.next("loaded");
                            return of(true);
                        }),
                        catchError((error) => {
                            console.error(error);
                            this.fetchResultSubject.next(null);
                            this._checkingRefDataLoaded = false;
                            return of(false);
                        })
                    );
                }
                else {
                    //if not expired we already raised the event from IndexDb check
                    this._checkingRefDataLoaded = false;
                    this.fetchResultSubject.next("loaded not expired");
                    return of(true);
                }
            })
        );
    }

    forceRefresh(companyId: string) {
        if (!companyId) {
            return;
        }
        this.fetch(companyId, true).subscribe();
    }

    private getActiveRefData(): Observable<DQOIReferenceData | null> {
        return this.dbService.getByKey(CommonValues.Database_FjxRefData, CommonValues.Database_FjxRefDataId)
            .pipe(map((refdataRow: RefDataTableRow) => {
                if (!refdataRow || Object.keys(refdataRow.refdata).length === 0) {
                    return null;
                }
                return new DQOIReferenceData(refdataRow.refdata);
            }));
    }

    isCacheExpired(): Observable<boolean> {
        const cacheItem = this.storageService.get<RefDataCacheItem>(CommonValues.StorageKeys_ReferenceDataCacheInfo);

        if (cacheItem && cacheItem.cacheExpiresAtDate && cacheItem.currentDbDate) {
            const isExpired = new Date(cacheItem.currentDbDate) > new Date(cacheItem.cacheExpiresAtDate);
            return of(isExpired);
        }
        return this.getReferenceDataUpdateDate().pipe(
            switchMap((data: any) => {
                const newCacheItem = new RefDataCacheItem();
                newCacheItem.currentDbDate = new Date(data.primaryReturn);
                return this.getActiveRefData().pipe(
                    map((refDataArr: DQOIReferenceData | null) => {
                        newCacheItem.cacheExpiresAtDate = new Date(refDataArr?.cacheExpiresAt);
                        if (!newCacheItem.cacheExpiresAtDate) {
                            return true;
                        }
                        this.storageService.set(new CacheItem(CommonValues.StorageKeys_ReferenceDataCacheInfo, newCacheItem, CacheType.Local));
                        const isExpired = newCacheItem.currentDbDate > newCacheItem.cacheExpiresAtDate;
                        return isExpired;
                    })
                );
            })
        );
    }

    getReferenceDataUpdateDate(): Observable<any> {
        return this.doqiService.getReferenceDataUpdateDate();
    }

    private fetch(companyId: string, alwaysFetch: boolean = false): Observable<DQOIReferenceData> {
        if ((this.fetching && !alwaysFetch) || !companyId) {
            return null;
        }
        this.fetching = true;
        return this.doqiService.getReferenceData(companyId).pipe(tap(data => {
            let arr: DQOIReferenceData[] = [];
            let storeData = new DQOIReferenceData(data);
            arr.push(storeData);
            //we set the store but it is not used internally
            this.store.set(arr);
            this.store.setActive(storeData.id);
            this._referenceDataUpdated.next(new DQOIReferenceData(data));
            this.fetching = false;
            //this is indexedDB
            const tableEntry = new RefDataTableRow(CommonValues.Database_FjxRefDataId, storeData);
            this.dbService.update(CommonValues.Database_FjxRefData, tableEntry).subscribe();
            localStorage.setItem(CommonValues.StorageKeys_ReferenceDataLoaded, Date.now().toString());
            if (environment.debug) {
                console.debug("%c Loaded RefData from API Request", CommonValues.LoggingMessage_ColorTeal);
            }
        }));
    }

    private getCaller() {
        const err = new Error();
        const stack = err.stack || '';
        const stackLines = stack.split('\n');
        // Adjust the index based on the environment
        const callerLine = stackLines[3] || '';
        const callerMatch = callerLine.match(/at (.*)/);
        return callerMatch ? callerMatch[1] : 'unknown';
    }

}
